import { resolveId } from '@medplum/core'
import type { Reference, ServiceRequest } from '@medplum/fhirtypes'
import { DocumentCategory } from '@seen/core/models/document-references/constants'
import type { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import {
  FOLDERS,
  type Folder,
} from '@seen/core/models/document-references/folder'
import { Order } from '@seen/core/models/orders/Order'
import type { DocumentReviewTask } from '@seen/core/models/tasks/standard/documentReviewTask'
import { compact } from 'lodash-es'
import { useCallback, useMemo } from 'react'
import { useFormState } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { useDidChange } from '#src/hooks/useDidChange.ts'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useForm } from '#src/hooks/useForm.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

const documentReferenceFormSchema = z.object({
  subject: z.any().nullable(),
  categories: z.array(z.nativeEnum(DocumentCategory)),
  orders: z.array(z.string()),
  folderId: z.string().nullable(),
})

export type DocumentReferenceFormValues = z.infer<
  typeof documentReferenceFormSchema
>

// The form holds the folder id so react-hook-form compares primitives; the
// model setter wants the Folder itself, and `undefined` (not null) is what
// clears the tag.
export function folderFromId(folderId: string | null): Folder | undefined {
  return folderId ? FOLDERS.find((folder) => folder.id === folderId) : undefined
}

function getDefaultValues(
  docRef: ExtendedDocumentReference
): DocumentReferenceFormValues {
  return {
    subject: docRef.subject ?? null,
    categories: docRef.categories ?? [],
    orders: compact(docRef.orders?.map(resolveId) ?? []),
    folderId: docRef.folder?.id ?? null,
  }
}

interface UseDocumentFormOptions {
  docRef: ExtendedDocumentReference | undefined
  task?: DocumentReviewTask
  onSubmitSuccess?: () => void
  isActionLoading?: boolean
}

export function useDocumentForm({
  docRef,
  task,
  onSubmitSuccess,
  isActionLoading = false,
}: UseDocumentFormOptions) {
  const { isPending: isTransactionPending, mutate: mutateTransaction } =
    useTransactionMutation()
  const patchDocument = usePatchMutation<ExtendedDocumentReference>()

  const defaultValues = useMemo(
    () =>
      docRef
        ? getDefaultValues(docRef)
        : {
            subject: null,
            categories: [],
            orders: [],
            folderId: null,
          },
    [docRef]
  )

  const form = useForm({
    schema: documentReferenceFormSchema,
    defaultValues,
  })

  const docRefChanged = useDidChange(docRef?.reference?.reference)
  if (docRefChanged && docRef) {
    form.reset(getDefaultValues(docRef))
  }

  const linkedOrders = useSearch(
    Order,
    {
      _id: docRef?.orders?.map(resolveId) ?? [],
    },
    { query: { enabled: !!docRef?.orders?.length } }
  )

  const selectedSubject = form.watch('subject')
  const { data: availableOrders } = useSearch(
    Order,
    selectedSubject ? Order.byNonDraftOrCancelled(selectedSubject) : undefined,
    { query: { enabled: !!selectedSubject } }
  )

  const ordersById = useMemo(() => {
    const map = new Map<string, Reference<ServiceRequest>>()

    availableOrders?.forEach((order) => {
      if (order.id) {
        map.set(order.id, order.reference)
      }
    })
    linkedOrders.data?.forEach((order) => {
      if (order.id) {
        map.set(order.id, order.reference)
      }
    })
    return map
  }, [availableOrders, linkedOrders.data])

  const handleSubmitForm = useCallback(
    async (values: DocumentReferenceFormValues): Promise<boolean> => {
      if (!docRef) {
        return false
      }

      return new Promise<boolean>((resolve) => {
        mutateTransaction(
          (transaction) => {
            transaction.patch(docRef, (instance) => {
              instance.subject = values.subject ?? undefined
              instance.categories = values.categories ?? undefined
              const selectedOrders =
                values.orders
                  ?.map((orderId) => ordersById.get(orderId))
                  .filter(
                    (order): order is Reference<ServiceRequest> => !!order
                  ) ?? []
              instance.orders = selectedOrders.length
                ? selectedOrders
                : undefined
              instance.folder = folderFromId(values.folderId)
            })

            if (task && values.subject) {
              transaction.patch(task, (instance) => {
                instance.participant = values.subject
              })
            }
          },
          {
            onSuccess: () => {
              if (docRef) {
                form.reset(getDefaultValues(docRef))
              }
              toast.success('Saved document changes')
              onSubmitSuccess?.()
              resolve(true)
            },
            onError: (error) => {
              console.error('Failed to save document changes', error)
              toast.error('Failed to save document changes')
              resolve(false)
            },
          }
        )
      })
    },
    [docRef, form, ordersById, onSubmitSuccess, mutateTransaction, task]
  )

  const submit = useCallback(async (): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      void form.handleSubmit(
        async (values) => {
          const success = await handleSubmitForm(values)
          resolve(success)
        },
        () => resolve(false)
      )()
    })
  }, [form, handleSubmitForm])

  const formState = useFormState({ control: form.control })
  const isSubmitting =
    formState.isSubmitting || patchDocument.isPending || isTransactionPending
  const isSaving = isSubmitting
  const disableActions = isSaving || isActionLoading

  return {
    form,
    isSaving,
    isDirty: formState.isDirty,
    disableActions,
    submit,
    availableOrders,
    predictedSubject: docRef?.predictedSubject,
    predictedOrders: docRef?.predictedOrders,
  }
}
